import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  Flex,
  DrawerOverlay,
} from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AwardCriterionType, type AwardSubCriterionDto } from "../../../../../autogen/bff-api";
import {
  AwardSubCriterionFields,
  type AwardSubCriterionFormState,
  type AwardSubCriterionFormValues,
} from "../../../../../common/library/fields/AwardSubCriterionFields";
import { RequirementDialogHeader } from "../../../../../common/components/RequirementDialogHeader";

// t('New sub-criterion in {{criterion}}') t('Edit sub-criterion') t('Cancel') t('Save') t('Add sub-criterion')
// t('Sub-criterion weights are distributed within the criterion (total 100 %)')

export type { AwardSubCriterionFormValues };

interface Props {
  isOpen: boolean;
  isSaving: boolean;
  mode: "add" | "edit";
  /** The parent criterion's name, so the header says which criterion the weight is a share of. */
  criterionName: string;
  /** The parent criterion's type: a requirement under a price criterion is scored, never answered. */
  criterionType?: AwardCriterionType;
  defaultValues?: AwardSubCriterionFormValues;
  /**
   * The "keep this for later" rows, shown under the fields while a requirement is being written. Owned
   * by the step, which is where the library writes happen — this drawer stays presentational. Given the
   * form's live state, since a row that stores the weight should be able to name it.
   */
  libraryOptions?: (state: AwardSubCriterionFormState) => ReactNode;
  onSubmit: (values: Omit<AwardSubCriterionDto, "id">) => Promise<void>;
  onClose: () => void;
}

const FORM_ID = "award-sub-criterion-drawer-form";

export const AwardSubCriterionDrawer = ({
  isOpen,
  isSaving,
  mode,
  criterionName,
  criterionType,
  defaultValues,
  libraryOptions,
  onSubmit,
  onClose,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent data-testid="award-sub-criterion-drawer">
        <RequirementDialogHeader
          title={
            mode === "edit"
              ? t("Edit sub-criterion")
              : t("New sub-criterion in {{criterion}}", { criterion: criterionName })
          }
          subtitle={t("Sub-criterion weights are distributed within the criterion (total 100 %)")}
        />

        <DrawerBody>
          <AwardSubCriterionFields
            answer={criterionType === AwardCriterionType.Price ? "derived-from-price" : "asked"}
            formId={FORM_ID}
            defaultValues={defaultValues}
            onSubmit={onSubmit}
            // Only while writing a new one: editing a requirement the procurement already holds is not
            // the moment to decide it is worth keeping — the row's own action is.
            {...(mode === "add" && libraryOptions ? { children: libraryOptions } : {})}
          />
        </DrawerBody>

        <DrawerFooter borderTopWidth="1px" borderColor="smBorder">
          <Flex gap="3">
            <Button variant="outline" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button type="submit" form={FORM_ID} colorScheme="teal" isLoading={isSaving}>
              {t(mode === "edit" ? "Save" : "Add sub-criterion")}
            </Button>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
